import React from "react";
import styled from "styled-components";
import VintageTV from "./VintageTV";
import type { MediaItem, VintageTVProps } from "./VintageTV";
import chromeTVCutout from "../assets/optimized/chrome-tv-cutout.webp";

export interface ChromeTVProps extends Omit<VintageTVProps, 'width' | 'height' | 'aspectRatio'> {
    /** Media items to display in the TV */
    media?: MediaItem[];
    /** Horizontal position of TV screen as percentage (0-100) */
    screenLeft?: string;
    /** Vertical position of TV screen as percentage (0-100) */
    screenTop?: string;
    /** Width of TV screen as percentage of container */
    screenWidth?: string;
    /** Height of TV screen as percentage of container */
    screenHeight?: string;
    /** Aspect ratio of the TV screen */
    screenAspectRatio?: string;
}

const ChromeTV = ({
    media = [],
    screenLeft = "42%",
    screenTop = "60%",
    screenWidth = "84%",
    screenHeight = "86%",
    screenAspectRatio = "31/25",
    autoPlay = true,
    autoPlayInterval = 4000,
    transitionDuration = 300,
    showStatic,
}: ChromeTVProps) => {
    return (
        <Container>
            <FrameSizer>
                <ScreenWrapper
                    $left={screenLeft}
                    $top={screenTop}
                    $width={screenWidth}
                    $height={screenHeight}
                >
                    <VintageTV
                        media={media}
                        aspectRatio={screenAspectRatio}
                        width="100%"
                        height="100%"
                        autoPlay={autoPlay}
                        autoPlayInterval={autoPlayInterval}
                        transitionDuration={transitionDuration}
                        showOverlay={false}
                        showStatic={showStatic}
                    />
                </ScreenWrapper>
                <FrameImage src={chromeTVCutout} alt="" />
            </FrameSizer>
        </Container>
    );
};

export default ChromeTV;

// MARK: - Styled Components

const Container = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    align-items: stretch;
`;

const FrameSizer = styled.div`
    position: relative;
    width: 100%;
    flex-shrink: 0;
    
    /* Use the frame image to establish aspect ratio */
    &::before {
        content: '';
        display: block;
        width: 100%;
        padding-bottom: 75%; /* Adjust based on chrome-tv-cutout.png aspect ratio */
    }
`;

const FrameImage = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
    z-index: 10;
    pointer-events: none;
`;

const ScreenWrapper = styled.div<{
    $left: string;
    $top: string;
    $width: string;
    $height: string;
}>`
    position: absolute;
    left: ${({ $left }) => $left};
    top: ${({ $top }) => $top};
    width: ${({ $width }) => $width};
    height: ${({ $height }) => $height};
    transform: translate(-50%, -50%);
    z-index: 1;
    pointer-events: none;
    
    > * {
        pointer-events: auto;
    }
`;

